import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Link, makeStyles, Text, tokens } from '@fluentui/react-components';
import {
    ColorFill20Regular,
    DataBarVertical20Regular,
    Delete20Regular,
    DocumentCopy20Regular,
    Edit20Regular,
    Link20Regular,
    Open20Regular,
    QrCode20Regular,
} from '@fluentui/react-icons';
import { useDataService, useAsync } from '../app/DataServiceContext';
import { useGeo } from '../components/forms/useGeo';
import { formatDateTime, formatNumber, formatPeriod, formatRelative, yesNo } from '../lib/format';
import { getEventTypeLabel, getLogoPositionLabel, getQuestionTypeLabel, getStateMeta, questionsCount } from '../lib/pollLabels';
import { getPollQuestions, isChoiceType, isRangeType, isTextType } from '../lib/pollQuestions';
import PageHeader from '../components/ui/PageHeader';
import Section from '../components/ui/Section';
import StatusBadge from '../components/ui/StatusBadge';
import PollTypeBadge from '../components/ui/PollTypeBadge';
import CopyButton from '../components/ui/CopyButton';
import DescriptionList, { InlineValue } from '../components/ui/DescriptionList';
import ErrorBar from '../components/ui/ErrorBar';
import EmptyState from '../components/ui/EmptyState';
import { StatGrid, StatTile } from '../components/ui/StatTile';
import { BlockSkeleton } from '../components/ui/Skeletons';
import { usePollActions } from '../components/polls/usePollActions';
import { QUESTION_TYPE_ICONS } from '../components/forms/QuestionTypePicker';
import NotFoundPage from './NotFoundPage';

const useStyles = makeStyles({
    layout: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, 360px)',
        gap: '20px',
        alignItems: 'start',
        '@media (max-width: 1023px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        minWidth: 0,
        '@media (max-width: 767px)': {
            gap: '16px',
        },
    },
    description: {
        whiteSpace: 'pre-wrap',
        lineHeight: tokens.lineHeightBase400,
        color: tokens.colorNeutralForeground2,
    },
    questions: {
        display: 'flex',
        flexDirection: 'column',
    },
    question: {
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        columnGap: '14px',
        rowGap: '8px',
        padding: '16px 20px',
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        ':first-child': {
            borderTop: 'none',
        },
        '@media (max-width: 767px)': {
            padding: '14px 16px',
        },
    },
    questionIcon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        height: '36px',
        borderRadius: tokens.borderRadiusMedium,
        color: tokens.colorBrandForeground1,
        backgroundColor: tokens.colorBrandBackground2,
    },
    questionBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        minWidth: 0,
    },
    questionTitle: {
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase300,
        overflowWrap: 'anywhere',
    },
    questionMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexWrap: 'wrap',
    },
    options: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        margin: 0,
        padding: 0,
        listStyle: 'none',
    },
    option: {
        paddingTop: '3px',
        paddingBottom: '3px',
        paddingLeft: '10px',
        paddingRight: '10px',
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
        backgroundColor: tokens.colorNeutralBackground3,
        borderRadius: tokens.borderRadiusCircular,
    },
    hint: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    swatchRow: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
    },
    swatch: {
        width: '18px',
        height: '18px',
        borderRadius: tokens.borderRadiusSmall,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        flexShrink: 0,
    },
    logo: {
        maxHeight: '40px',
        maxWidth: '140px',
        objectFit: 'contain',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusSmall,
        padding: '2px',
        backgroundColor: '#fff',
    },
    quickLinks: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
    },
    quickLink: {
        flexGrow: 1,
        flexShrink: 0,
        flexBasis: 'auto',
        justifyContent: 'center',
        minHeight: '44px',
        whiteSpace: 'nowrap',
    },
});

const Swatch: React.FC<{ color?: string | null; label?: string }> = ({ color, label }) => {
    const styles = useStyles();
    if (!color) return <>—</>;
    return (
        <span className={styles.swatchRow}>
            <span className={styles.swatch} style={{ background: color }} aria-hidden />
            <span style={{ fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200 }}>{label ?? color}</span>
        </span>
    );
};

const PollDetailsPage: React.FC = () => {
    const styles = useStyles();
    const navigate = useNavigate();
    const { id = '' } = useParams<{ id: string }>();
    const dataService = useDataService();

    const poll = useAsync(() => dataService.getPoll(id), [id]);
    const geo = useGeo(poll.data?.GeoRegionId ?? '');
    const actions = usePollActions({ onDeleted: () => navigate('/polls', { replace: true }) });

    const data = poll.data;
    const questions = React.useMemo(() => getPollQuestions(data), [data]);
    const surveyUrl = data ? actions.surveyUrl(data) : '';
    const regionName = geo.regions.find((r) => r.Id === data?.GeoRegionId)?.Name;
    const territory = geo.territories.find((t) => t.Id === data?.GeoTerritoryId);

    if (!poll.loading && poll.error && /404|не найден/i.test(poll.error)) {
        return <NotFoundPage />;
    }

    const encoded = encodeURIComponent(id);
    const goto = (suffix: string) => () => navigate(`/polls/${encoded}${suffix}`);

    return (
        <>
            <PageHeader
                backTo={{ to: '/polls', label: 'Все опросы' }}
                title={data?.Title ?? (poll.loading ? 'Загрузка…' : 'Опрос')}
                subtitle={data ? (
                    <>
                        {data.Number !== undefined && <>№ {data.Number} · </>}
                        Создан {formatDateTime(data.Created)}
                        {data.AuthorName || data.AuthorId ? <> · {data.AuthorName ?? data.AuthorId}</> : null}
                    </>
                ) : undefined}
                badges={data && (
                    <>
                        <StatusBadge state={data.State} />
                        <PollTypeBadge poll={data} />
                    </>
                )}
                actions={data ? [
                    { key: 'edit', label: 'Редактировать', icon: <Edit20Regular />, appearance: 'primary', onClick: goto('/edit') },
                    { key: 'results', label: 'Результаты', icon: <DataBarVertical20Regular />, onClick: goto('/results') },
                    { key: 'qr', label: 'QR-код', icon: <QrCode20Regular />, onClick: goto('/qr') },
                    { key: 'branding', label: 'Оформление', icon: <ColorFill20Regular />, onClick: goto('/branding') },
                    { key: 'open', label: 'Открыть опрос', icon: <Open20Regular />, overflowOnly: true, disabled: !surveyUrl, onClick: () => actions.openSurvey(data), dividerBefore: true },
                    { key: 'copy', label: 'Копировать ссылку', icon: <Link20Regular />, overflowOnly: true, disabled: !surveyUrl, onClick: () => { void actions.copyLink(data); } },
                    { key: 'duplicate', label: 'Дублировать', icon: <DocumentCopy20Regular />, overflowOnly: true, disabled: actions.duplicating, onClick: () => { void actions.duplicate(data); } },
                    { key: 'delete', label: 'Удалить', icon: <Delete20Regular />, overflowOnly: true, onClick: () => actions.requestDelete(data), dividerBefore: true },
                ] : []}
            />

            <ErrorBar message={poll.error} title="Не удалось загрузить опрос" onRetry={poll.reload} />

            {poll.loading && !data && (
                <Section><BlockSkeleton lines={6} /></Section>
            )}

            {data && (
                <>
                    <StatGrid>
                        <StatTile label="Ответов" value={formatNumber(data.ResponseCount)} hint={data.SingleResponse ? 'по одному на участника' : 'повторные разрешены'} />
                        <StatTile label="Вопросов" value={String(questions.length)} hint={data.Quick || data.Type === 0 ? 'быстрый опрос' : 'комплексный опрос'} />
                        <StatTile label="Период" value={formatPeriod(data.OpensAt, data.ClosesAt)} compact hint={data.ClosesAt ? `закрывается ${formatRelative(data.ClosesAt)}` : undefined} />
                        <StatTile label="Обновлён" value={data.Updated ? formatRelative(data.Updated) : formatRelative(data.Created)} compact hint={formatDateTime(data.Updated ?? data.Created)} />
                    </StatGrid>

                    <div className={styles.layout}>
                        <div className={styles.column}>
                            {data.Description && (
                                <Section title="Описание">
                                    <Text className={styles.description}>{data.Description}</Text>
                                </Section>
                            )}

                            <Section
                                title="Вопросы"
                                description={questionsCount(questions.length)}
                                flush
                                action={<Button appearance="subtle" icon={<Edit20Regular />} onClick={goto('/edit')}>Изменить</Button>}
                            >
                                {questions.length === 0 ? (
                                    <div style={{ padding: 16 }}>
                                        <EmptyState
                                            icon={QUESTION_TYPE_ICONS[4]}
                                            title="В опросе нет вопросов"
                                            description="Ответ API не содержит вопросов. Добавьте их в режиме редактирования."
                                            action={{ label: 'Редактировать', onClick: goto('/edit'), icon: <Edit20Regular /> }}
                                        />
                                    </div>
                                ) : (
                                    <ol className={styles.questions} style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                                        {questions.map((question, index) => (
                                            <li key={question.key} className={styles.question}>
                                                <span className={styles.questionIcon} aria-hidden>{QUESTION_TYPE_ICONS[question.Type]}</span>
                                                <div className={styles.questionBody}>
                                                    <Text className={styles.questionTitle}>{index + 1}. {question.Title || 'Без текста'}</Text>
                                                    <div className={styles.questionMeta}>
                                                        <Badge appearance="tint" color="brand" size="small" shape="rounded">{getQuestionTypeLabel(question.Type)}</Badge>
                                                        {question.Required
                                                            ? <Badge appearance="outline" color="subtle" size="small" shape="rounded">Обязательный</Badge>
                                                            : <Badge appearance="ghost" color="subtle" size="small" shape="rounded">Необязательный</Badge>}
                                                        {isRangeType(question.Type) && (
                                                            <Text className={styles.hint}>Шкала {question.MinValue}–{question.MaxValue}</Text>
                                                        )}
                                                    </div>
                                                    {isChoiceType(question.Type) && question.Options.some((o) => o.Text) && (
                                                        <ul className={styles.options}>
                                                            {question.Options.filter((o) => o.Text).map((option, i) => (
                                                                <li key={option.Id ?? `${option.Text}-${i}`} className={styles.option}>{option.Text}</li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                    {isTextType(question.Type) && question.Placeholder && (
                                                        <Text className={styles.hint}>Подсказка: «{question.Placeholder}»</Text>
                                                    )}
                                                </div>
                                            </li>
                                        ))}
                                    </ol>
                                )}
                            </Section>
                        </div>

                        <div className={styles.column}>
                            <Section title="Ссылка для участников">
                                {surveyUrl ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                        <Link href={surveyUrl} target="_blank" rel="noopener noreferrer" style={{ overflowWrap: 'anywhere' }}>
                                            {surveyUrl}
                                        </Link>
                                        <div className={styles.quickLinks}>
                                            <CopyButton value={surveyUrl} label="Копировать ссылку" successMessage="Ссылка скопирована" text="Копировать" size="medium" appearance="secondary" className={styles.quickLink} />
                                            <Button appearance="secondary" icon={<QrCode20Regular />} className={styles.quickLink} onClick={goto('/qr')}>QR-код</Button>
                                            <Button appearance="secondary" icon={<Open20Regular />} className={styles.quickLink} onClick={() => actions.openSurvey(data)}>Открыть</Button>
                                        </div>
                                    </div>
                                ) : (
                                    <Text className={styles.hint}>Slug ещё не назначен, ссылка появится после публикации опроса.</Text>
                                )}
                            </Section>

                            <Section title="Сведения">
                                <DescriptionList items={[
                                    { term: 'Статус', details: <InlineValue><StatusBadge state={data.State} size="small" />{data.StatusChangedAt && <Text className={styles.hint}>с {formatDateTime(data.StatusChangedAt)}</Text>}</InlineValue> },
                                    ...(data.StatusPrevious !== undefined && data.StatusPrevious !== null
                                        ? [{ term: 'Предыдущий статус', details: getStateMeta(data.StatusPrevious).label }]
                                        : []),
                                    { term: 'Тип мероприятия', details: getEventTypeLabel(data.EventTypeCode) },
                                    { term: 'Регион', details: regionName ?? (data.GeoRegionId ? (geo.loadingRegions ? 'Загрузка…' : data.GeoRegionId) : '—') },
                                    ...(data.GeoTerritoryId
                                        ? [{ term: 'Территория', details: territory ? `${territory.Name}${territory.City ? `, ${territory.City}` : ''}` : (geo.loadingTerritories ? 'Загрузка…' : data.GeoTerritoryId) }]
                                        : []),
                                    { term: 'Slug', details: <InlineValue><span>{data.Slug ?? '—'}</span>{data.Slug && <CopyButton value={surveyUrl} label="Копировать ссылку на опрос" successMessage="Ссылка скопирована" />}</InlineValue>, mono: true },
                                    { term: 'Slug Deck', details: <InlineValue><span>{data.SlugDeck ?? '—'}</span>{data.SlugDeck && <CopyButton value={data.SlugDeck} label="Копировать Slug Deck" successMessage="Slug Deck скопирован" />}</InlineValue>, mono: true },
                                    { term: 'Один ответ', details: yesNo(data.SingleResponse) },
                                    { term: 'Показ результатов', details: yesNo(data.CompletedDisplayResults) },
                                    { term: 'Сообщение в конце', details: data.CompletedMessage || '—' },
                                    { term: 'Создан', details: formatDateTime(data.Created) },
                                    { term: 'Обновлён', details: data.Updated ? formatDateTime(data.Updated) : '—' },
                                ]} />
                            </Section>

                            <Section
                                title="Оформление"
                                action={<Button appearance="subtle" icon={<ColorFill20Regular />} onClick={goto('/branding')}>Настроить</Button>}
                            >
                                <DescriptionList items={[
                                    {
                                        term: 'Фон',
                                        details: data.BrandingBackgroundType === 1
                                            ? <Swatch color={`linear-gradient(135deg, ${data.BrandingBackgroundGradient ?? '#fff, #fff'})`} label={`Градиент ${data.BrandingBackgroundGradient ?? ''}`} />
                                            : <Swatch color={data.BrandingBackgroundColor ?? undefined} />,
                                    },
                                    {
                                        term: 'Логотип',
                                        details: data.BrandingLogoUrl
                                            ? <img src={data.BrandingLogoUrl} alt="Логотип опроса" className={styles.logo} />
                                            : 'Не задан',
                                    },
                                    ...(data.BrandingLogoUrl ? [{ term: 'Позиция логотипа', details: `${getLogoPositionLabel(data.BrandingLogoPositionCode)}, ${data.BrandingLogoSize ?? '—'} px` }] : []),
                                    { term: 'QR-код на экране', details: yesNo(data.QrcodeVisible) },
                                    ...(data.QrcodeVisible ? [
                                        { term: 'Цвет QR', details: <Swatch color={data.QrcodeForeground ?? undefined} /> },
                                        { term: 'Фон QR', details: <Swatch color={data.QrcodeBackground ?? undefined} /> },
                                    ] : []),
                                ]} />
                            </Section>
                        </div>
                    </div>
                </>
            )}

            {actions.dialog}
        </>
    );
};

export default PollDetailsPage;
